import { Pause, Play } from '@tunasong/icons'
import type { Audio, Persisted, Video } from '@tunasong/schemas'
import { IconButton } from '@tunasong/ui-lib'
import type { FC, MouseEvent } from 'react'
import { useGlobalPlayerState } from './global-player-state.hook.js'
import styles from './player-button.module.css'

export interface GlobalPlayToggleProps {
  media: Persisted<Audio | Video>
  /** What started playback (a poll, a playlist), for the global player's backlink. */
  sourceEntityId?: string
  size?: 'small' | 'medium'
}

/**
 * Play / Pause for one clip in the app-wide player, so starting it stops whatever was playing anywhere. It only
 * subscribes to whether this clip is the one playing, so a long list of these stays idle during playback.
 */
export const GlobalPlayToggle: FC<GlobalPlayToggleProps> = props => {
  const { media, sourceEntityId, size = 'medium' } = props
  const isPlaying = useGlobalPlayerState(state => state.playing && state.active?.media.id === media.id)
  const commands = useGlobalPlayerState(state => state.commands)
  const label = media.name ?? 'audio'

  /** Rendered inside links and vote labels: the press must neither navigate nor activate the label's control. */
  const handleClick = (ev: MouseEvent) => {
    ev.preventDefault()
    ev.stopPropagation()
    if (isPlaying) {
      commands?.pause()
      return
    }
    commands?.playNow([media], sourceEntityId)
  }

  return (
    <IconButton
      className={styles.iconButton}
      color="primary"
      size={size}
      aria-label={`${isPlaying ? 'Pause' : 'Play'} ${label}`}
      title={media.name}
      disabled={!(commands && media.storageUrls?.url)}
      onClick={handleClick}
    >
      {isPlaying ? <Pause /> : <Play />}
    </IconButton>
  )
}

export default GlobalPlayToggle
